/**
 * Frontend dla wyszukiwarki repozytorium:
 * - toggle rozwijanego panelu filtrów
 * - custom multi-select dropdown dla kategorii (z wyszukiwaniem)
 * - synchronizacja klasy is-active na chipach (tag, format)
 *
 * Paginacja: standardowa WP (page reload) - obsługuje ją $myTheme->pagination()
 * z PHP, brak osobnego JS.
 */

const closeFilterPanel = (target, btn) => {
    target.setAttribute('hidden', '');
    target.classList.remove('is-open');
    if (btn) {
        btn.classList.remove('is-open');
        btn.setAttribute('aria-expanded', 'false');
    }
    document.body.classList.remove('repo-modal-open');
};

const openFilterPanel = (target, btn) => {
    target.removeAttribute('hidden');
    target.classList.add('is-open');
    if (btn) {
        btn.classList.add('is-open');
        btn.setAttribute('aria-expanded', 'true');
    }
    document.body.classList.add('repo-modal-open');
};

const initRepoToggle = () => {
    document.querySelectorAll('[data-target]').forEach((btn) => {
        if (!btn.classList.contains('repoSearch__toggle')) return;
        btn.addEventListener('click', (e) => {
            e.preventDefault();
            const target = document.querySelector(btn.dataset.target);
            if (!target) return;
            if (target.hasAttribute('hidden')) {
                openFilterPanel(target, btn);
            } else {
                closeFilterPanel(target, btn);
            }
        });
    });

    // X w prawym górnym rogu modala (widoczny tylko na mobile)
    document.querySelectorAll('.repoSearch__modalClose').forEach((closeBtn) => {
        closeBtn.addEventListener('click', () => {
            const target = closeBtn.closest('.repoSearch__advanced');
            const toggle = document.querySelector('.repoSearch__toggle');
            if (target) closeFilterPanel(target, toggle);
        });
    });

    // ESC zamyka modal filtrów (gdy otwarty)
    document.addEventListener('keydown', (e) => {
        if (e.key !== 'Escape') return;
        const target = document.querySelector('.repoSearch__advanced.is-open');
        const toggle = document.querySelector('.repoSearch__toggle.is-open');
        if (target) closeFilterPanel(target, toggle);
    });
};

/**
 * Polska pluralizacja: 1 / 2-4 (poza 12-14) / 5+.
 * Zwraca odpowiednią formę dla podanej liczby.
 */
const polishPlural = (n, [one, few, many]) => {
    const count = Math.abs(n);
    if (count === 1) return one;
    const mod10 = count % 10;
    const mod100 = count % 100;
    if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return few;
    return many;
};

const initRepoCatPicker = () => {
    document.querySelectorAll('[data-component="cat-picker"]').forEach((picker) => {
        const trigger = picker.querySelector('.repoCatPicker__trigger');
        const panel = picker.querySelector('.repoCatPicker__panel');
        const label = picker.querySelector('.repoCatPicker__label');
        const searchInput = picker.querySelector('.repoCatPicker__search');
        const items = picker.querySelectorAll('.repoCatPicker__item');
        const checkboxes = picker.querySelectorAll('.repoCatPicker__option input[type="checkbox"]');
        const clearBtn = picker.querySelector('.repoCatPicker__clear');
        const closeBtn = picker.querySelector('.repoCatPicker__close');

        if (!trigger || !panel || !label) return;

        const defaultLabel = trigger.dataset.defaultLabel || label.textContent.trim();

        const updateLabel = () => {
            const checked = Array.from(checkboxes).filter((cb) => cb.checked);
            const count = checked.length;
            if (count === 0) {
                label.textContent = defaultLabel;
            } else if (count === 1) {
                const span = checked[0].closest('.repoCatPicker__option').querySelector('.repoCatPicker__optionLabel');
                label.textContent = span ? span.textContent.trim() : `1 kategoria`;
            } else if (count === 2) {
                label.textContent = checked
                    .map((cb) => cb.closest('.repoCatPicker__option').querySelector('.repoCatPicker__optionLabel').textContent.trim())
                    .join(', ');
            } else {
                const word = polishPlural(count, ['kategoria', 'kategorie', 'kategorii']);
                label.textContent = `${count} ${word}`;
            }
        };

        const open = () => {
            picker.classList.add('is-open');
            panel.removeAttribute('hidden');
            trigger.setAttribute('aria-expanded', 'true');
            if (searchInput) setTimeout(() => searchInput.focus(), 50);
        };

        const close = () => {
            picker.classList.remove('is-open');
            panel.setAttribute('hidden', '');
            trigger.setAttribute('aria-expanded', 'false');
            if (searchInput) {
                searchInput.value = '';
                items.forEach((item) => item.classList.remove('is-hidden'));
            }
        };

        trigger.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            if (picker.classList.contains('is-open')) {
                close();
            } else {
                open();
            }
        });

        // Click outside zamyka dropdown
        document.addEventListener('click', (e) => {
            if (picker.contains(e.target)) return;
            if (picker.classList.contains('is-open')) close();
        });

        // Klik wewnątrz panelu nie zamyka dropdownu
        panel.addEventListener('click', (e) => {
            e.stopPropagation();
        });

        // ESC zamyka
        document.addEventListener('keydown', (e) => {
            if (e.key === 'Escape' && picker.classList.contains('is-open')) {
                close();
                trigger.focus();
            }
        });

        // Update label przy zmianie checkboxa
        checkboxes.forEach((cb) => cb.addEventListener('change', updateLabel));

        // Wyczyść wybór
        if (clearBtn) {
            clearBtn.addEventListener('click', () => {
                checkboxes.forEach((cb) => { cb.checked = false; });
                updateLabel();
            });
        }

        // Zamknij
        if (closeBtn) {
            closeBtn.addEventListener('click', close);
        }

        // Search wewnątrz listy
        if (searchInput) {
            searchInput.addEventListener('input', () => {
                const q = searchInput.value.toLowerCase().trim();
                items.forEach((item) => {
                    if (!q) {
                        item.classList.remove('is-hidden');
                        return;
                    }
                    const text = (item.textContent || '').toLowerCase();
                    item.classList.toggle('is-hidden', !text.includes(q));
                });
            });
        }
    });
};

const initRepoChips = () => {
    document.querySelectorAll('.repoFilter__chip input[type="checkbox"]').forEach((input) => {
        const label = input.closest('.repoFilter__chip');
        if (!label) return;
        const syncState = () => label.classList.toggle('is-active', input.checked);
        input.addEventListener('change', syncState);
        syncState();
    });
};

const initRepoSearch = () => {
    initRepoToggle();
    initRepoCatPicker();
    initRepoChips();
};

document.addEventListener('DOMContentLoaded', initRepoSearch, false);

export { initRepoSearch };
